import images from '@assets/images/index'
import { useQuery } from '@tanstack/react-query'
import { PaginationState } from '@tanstack/react-table'
import { Col, Flex, Row, Spin } from 'antd'
import parse from 'html-react-parser'
import { Resizable } from 're-resizable'
import { useEffect, useState } from 'react'

import {
  Entities_Admin_AppFeedback_FeedbackDetail,
  Entities_Admin_UserFeedback_Details,
  Entities_Admin_UserFeedback_ImageDetail
} from '@suntex.fim/api-client'
import { isAuthenticated, useAuth } from '@suntex.fim/auth/react'
import { useFramework, useRotateDevice } from '@suntex.fim/framework'
import { Modal, TablePagination } from '@suntex.fim/ui'

interface Image {
  data: string // Assuming the `data` property is a base64 string
}

function FeedbackMessages() {
  const { api } = useFramework()
  useRotateDevice()
  const [mode, setMode] = useState<'userFeedback' | 'appFeedback'>(
    'userFeedback'
  )
  const [pagination, setPagination] = useState<PaginationState>({
    pageSize: 25,
    pageIndex: 0
  })
  const [activeMessage, setActiveMessage] = useState<number | null>(null)
  const { login } = useAuth(isAuthenticated)

  const { data: userFeedbackData, isLoading: isUserFeedbackLoading } = useQuery(
    {
      queryKey: [
        'admin',
        'feedback',
        'userFeedback',
        pagination.pageIndex,
        pagination.pageSize
      ],
      queryFn: async () => {
        const response = await api.get_Admin_UserFeedback({
          pageNumber: pagination.pageIndex + 1,
          pageSize: pagination.pageSize
        })
        return {
          messages: response.responses || [],
          totalCount: response.totalCount || 0
        }
      },
      enabled: login.id > 0 && mode === 'userFeedback'
    }
  )

  const { data: appFeedbackData, isLoading: isAppFeedbackLoading } = useQuery({
    queryKey: [
      'admin',
      'feedback',
      'appFeedback',
      pagination.pageIndex,
      pagination.pageSize
    ],
    queryFn: async () => {
      const response = await api.get_Admin_AppFeedback({
        pageNumber: pagination.pageIndex + 1,
        pageSize: pagination.pageSize
      })
      return {
        messages: response.responses || [],
        totalCount: response.totalCount || 0
      }
    },
    enabled: login.id > 0 && mode === 'appFeedback'
  })

  const isLoading =
    mode === 'userFeedback' ? isUserFeedbackLoading : isAppFeedbackLoading
  const messagesDisplay =
    mode === 'userFeedback'
      ? (userFeedbackData?.messages ?? [])
      : (appFeedbackData?.messages ?? [])
  const userFeedbackCount = userFeedbackData?.totalCount ?? 0
  const appFeedbackCount = appFeedbackData?.totalCount ?? 0

  useEffect(() => {
    if (messagesDisplay.length > 0) {
      setActiveMessage(messagesDisplay[0]?.id || null)
    }
  }, [messagesDisplay])

  const activeMessageContent = messagesDisplay.find(
    (message) => message.id === activeMessage
  )

  const getImageSrc = (
    feedback: Entities_Admin_UserFeedback_Details
  ): string | undefined => {
    return feedback.data.images && feedback.data.images[0]?.data
      ? `data:image/png;base64,${feedback.data.images[0].data}`
      : undefined
  }
  const hasImages = (
    feedback: Entities_Admin_UserFeedback_Details
  ): boolean => {
    return (
      !!feedback.data.images &&
      Array.isArray(feedback.data.images) &&
      feedback.data.images.length > 0
    )
  }
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [selectedImage, setSelectedImage] =
    useState<Entities_Admin_UserFeedback_ImageDetail | null>(null) // Use the Image type

  const showModal = (image: Entities_Admin_UserFeedback_ImageDetail) => {
    setSelectedImage(image) // Set the clicked image
    setIsModalOpen(true) // Open the modal
  }

  const handleOk = () => {
    setIsModalOpen(false)
    setSelectedImage(null) // Clear the selected image
  }

  const handleCancel = () => {
    setIsModalOpen(false)
    setSelectedImage(null) // Clear the selected image
  }
  return (
    <div className="Feedback-Messages-main">
      <div className="main-heading">
        <h1>Messages</h1>
        <div className="tabs">
          <ul className="tabs-nav-list">
            <li
              className={`tabs-btn ${mode === 'userFeedback' ? 'tabs-btn-active' : ''}`}
              onClick={() => {
                setMode('userFeedback')
                setPagination({ pageIndex: 0, pageSize: 25 })
              }}
            >
              <Flex gap={10} align="center">
                <images.Feedbackicon />
                <span>User Feedback {userFeedbackCount}</span>
              </Flex>
            </li>
            <li
              className={`tabs-btn ${mode === 'appFeedback' ? 'tabs-btn-active' : ''}`}
              onClick={() => {
                setMode('appFeedback')
                setPagination({ pageIndex: 0, pageSize: 25 })
              }}
            >
              <Flex gap={10} align="center">
                <images.Appfeedback />
                <span>App Feedback {appFeedbackCount}</span>
              </Flex>
            </li>
          </ul>
        </div>
      </div>
      {isLoading && messagesDisplay.length === 0 ? (
        <Spin style={{ width: '100%', marginTop: 40 }} />
      ) : messagesDisplay.length > 0 && !isLoading ? (
        <Row className="main-inbox feedback-main-inbox open-message-mobile">
          <Resizable className="message-resize-box" enable={{ right: true }}>
            <Col flex="400px" className="message-list message-list-left">
              {messagesDisplay.length > 0 &&
                messagesDisplay.map((feedback) => (
                  <div
                    tabIndex={0}
                    role="button"
                    onKeyDown={() => setActiveMessage(feedback.id || null)}
                    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                    key={feedback.id}
                    className={`message-list-tool ${feedback.id === activeMessage ? 'active' : ''}`}
                    onClick={() => setActiveMessage(feedback.id || null)}
                  >
                    <div className="message-list-item__details">
                      <div className="to">
                        <div className="to__name message-name-mobile">
                          LoginId:{' '}
                          <span className="fw-bold">{feedback.loginId}</span>
                        </div>
                      </div>
                      <div className="subject fw-bold message-name-mobile">
                        {mode === 'userFeedback'
                          ? (feedback as Entities_Admin_UserFeedback_Details)
                              .data.subject
                          : (
                              feedback as Entities_Admin_AppFeedback_FeedbackDetail
                            ).switchReason.reasonTitle}
                      </div>
                      {mode === 'userFeedback' &&
                        hasImages(
                          feedback as Entities_Admin_UserFeedback_Details
                        ) && (
                          <div className="desc message-box-img">
                            <img
                              src={getImageSrc(
                                feedback as Entities_Admin_UserFeedback_Details
                              )}
                              alt={`Image ${feedback.loginId}`}
                              style={{ width: '300px', height: 'auto' }}
                            />
                          </div>
                        )}
                    </div>
                  </div>
                ))}
              <div className="message-box-pagination">
                <TablePagination
                  manualPagination={false}
                  page={pagination.pageIndex + 1}
                  pageSize={pagination.pageSize}
                  isLoading={isLoading}
                  totalEntries={
                    mode === 'userFeedback'
                      ? userFeedbackCount
                      : appFeedbackCount
                  }
                  setPage={(pageIndex) => {
                    setPagination((prev) => ({
                      ...prev,
                      pageIndex: pageIndex - 1
                    }))
                  }}
                  setPageSize={(pageSize) => {
                    setPagination((prev) => ({
                      ...prev,
                      pageIndex: 0,
                      pageSize
                    }))
                  }}
                  totalRecord={
                    mode === 'userFeedback'
                      ? userFeedbackCount
                      : appFeedbackCount
                  }
                />
              </div>
            </Col>
          </Resizable>
          <Col flex="auto" className="message-body">
            {activeMessageContent && (
              <Flex vertical className="open-message text-chalkboard">
                <div className="subject">
                  <h4 className="subject__label fw-bold message-name-mobile">
                    {mode === 'userFeedback'
                      ? parse(
                          (
                            activeMessageContent as Entities_Admin_UserFeedback_Details
                          ).data.subject || ''
                        )
                      : parse(
                          (
                            activeMessageContent as Entities_Admin_AppFeedback_FeedbackDetail
                          ).switchReason.reasonTitle || ''
                        )}
                  </h4>
                </div>
                <hr />
                <div>
                  <div className="email">
                    {mode === 'userFeedback' &&
                      (
                        activeMessageContent as Entities_Admin_UserFeedback_Details
                      ).data.images
                        ?.filter(
                          (
                            image
                          ): image is Entities_Admin_UserFeedback_ImageDetail =>
                            !!image.data
                        ) // Ensure `data` exists
                        .map((image, index) => (
                          <img
                            key={`${activeMessageContent.id}-${index}`}
                            src={`data:image/png;base64,${image.data}`}
                            alt={`feedback ${index + 1}`}
                            style={{
                              width: '300px',
                              height: 'auto',
                              cursor: 'pointer'
                            }}
                            onClick={() => showModal(image)} // Trigger modal on click
                          />
                        ))}
                    <p className="message-name-mobile">
                      {mode === 'userFeedback'
                        ? parse(
                            (
                              activeMessageContent as Entities_Admin_UserFeedback_Details
                            ).data.message || ''
                          )
                        : parse(
                            (
                              activeMessageContent as Entities_Admin_AppFeedback_FeedbackDetail
                            ).switchReason.reasonDescription || ''
                          )}
                    </p>
                  </div>
                  <Modal
                    title="Feedback Image"
                    open={isModalOpen}
                    onOk={handleOk}
                    onCancel={handleCancel}
                    footer={null}
                    width="75%"
                  >
                    {selectedImage && (
                      <img
                        src={`data:image/png;base64,${selectedImage.data}`}
                        alt="Selected Feedback"
                        style={{ width: '100%', height: 'auto' }}
                      />
                    )}
                  </Modal>
                </div>
              </Flex>
            )}
          </Col>
        </Row>
      ) : (
        <Spin spinning={isLoading} style={{ width: '100%' }} />
      )}
    </div>
  )
}

export default FeedbackMessages
